import type { SectionSampleFeedbackGradeEntry } from "@repo/app-types/entities/playbookProposalDetail";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { sectionIsNotApplicableValueToDisplayValue } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { FeedbackCell } from "./graderFeedback/feedbackCell.js";

interface SectionFeedbackGradingsTableProps {
  entries: readonly SectionSampleFeedbackGradeEntry[];
  isLoading?: boolean;
  isError?: boolean;
  onGradingSelect: (gradingId: GradingId) => void;
}

type SectionFeedbackGradingRow = SectionSampleFeedbackGradeEntry & {
  id: string;
};

const toDisplay = (grade: { is_not_applicable: boolean }): string =>
  sectionIsNotApplicableValueToDisplayValue(grade.is_not_applicable);

export const SectionFeedbackGradingsTable: FC<
  SectionFeedbackGradingsTableProps
> = (props) => {
  const { entries } = props;

  const rows = useMemo(
    (): SectionFeedbackGradingRow[] =>
      entries.map((e) => ({ ...e, id: e.feedback_id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<SectionFeedbackGradingRow>[] => [
      {
        id: "date",
        header: "Date",
        isRowHeader: true,
        renderCell: (entry) =>
          new Date(entry.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
      },
      {
        id: "submittedBy",
        header: "Submitted by",
        renderCell: (entry) => (
          <span className="text-gray-600">
            {entry.feedback_author_name ?? "-"}
          </span>
        ),
      },
      {
        id: "currentGrade",
        header: "Current",
        renderCell: (entry) => {
          if (!entry.current_grade) {
            return <span className="text-gray-400">-</span>;
          }
          const isCorrect =
            entry.current_grade.is_not_applicable ===
            entry.correct_grade.is_not_applicable;
          return (
            <FeedbackCell
              value={toDisplay(entry.current_grade)}
              commentary={entry.current_grade.commentary}
              correctness={{ isCorrect, isClose: false }}
            />
          );
        },
      },
      {
        id: "testGrade",
        header: "Test",
        renderCell: (entry) => {
          if (!entry.test_grade) {
            return <span className="text-gray-400">-</span>;
          }
          const isCorrect =
            entry.test_grade.is_not_applicable ===
            entry.correct_grade.is_not_applicable;
          return (
            <FeedbackCell
              value={toDisplay(entry.test_grade)}
              commentary={entry.test_grade.commentary}
              correctness={{ isCorrect, isClose: false }}
            />
          );
        },
      },
      {
        id: "correctGrade",
        header: "Correct",
        renderCell: (entry) => (
          <FeedbackCell
            value={toDisplay(entry.correct_grade)}
            commentary={entry.correct_grade.commentary}
          />
        ),
      },
    ],
    [],
  );

  return (
    <EntityTable
      aria-label="Feedback graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No feedback calls found."
      size={Size.SMALL}
      isFullBleed
      onEntityAction={(entry) => props.onGradingSelect(entry.grading_id)}
      getRowClassName={alignCellsTop}
    />
  );
};

function alignCellsTop(): string {
  return "relative align-top";
}
